import { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { Check, Edit2, PlusCircle, Trash2, X } from "react-feather";
import { Form, Formik } from "formik";
import { dateFormat } from "helpers/common";
import CommonButton from "components/shared/FormComponents/V2/commonV2/CommonButton";
import AppDatePicker from "components/shared/FormComponents/AppDatePicker";
import AppInputField from "components/shared/FormComponents/AppInputField";
import crossIcon from "../../../Assets/images/cross_icon.png";

const normalizeAssessmentDate = (assessmentDate) => ({
  assessment_date: typeof assessmentDate === "string"
    ? assessmentDate
    : assessmentDate?.assessment_date || assessmentDate?.date || "",
  assessment_time: typeof assessmentDate === "string" ? "" : assessmentDate?.assessment_time?.slice(0, 5) || ""
});

const AssessmentDatesModal = ({ show, onHide, assignedDates = [], onSave, isLoading }) => {
  const [assessmentDates, setAssessmentDates] = useState([]);
  const [editingDate, setEditingDate] = useState(null);
  const formikRef = useRef(null);

  useEffect(() => {
    if (!show) return;

    setAssessmentDates(assignedDates
      .map(normalizeAssessmentDate)
      .filter(({ assessment_date }) => Boolean(assessment_date))
    );
    setEditingDate(null);
    formikRef.current?.resetForm();
  }, [show, assignedDates]);

  const addAssessmentDate = ({ assessment_date, assessment_time }) => {
    if (!assessment_date || assessmentDates.some((date) => date.assessment_date === assessment_date)) return;

    setAssessmentDates((dates) => [...dates, { assessment_date, assessment_time }]);
    formikRef.current?.setFieldValue("newAssessmentDate", "");
    formikRef.current?.setFieldValue("newAssessmentTime", "");
  };

  const startEditingDate = (assessmentDate) => {
    setEditingDate(assessmentDate.assessment_date);
    formikRef.current?.setFieldValue("editedDate", assessmentDate.assessment_date);
    formikRef.current?.setFieldValue("editedAssessmentTime", assessmentDate.assessment_time);
  };

  const cancelEditingDate = () => {
    setEditingDate(null);
    formikRef.current?.setFieldValue("editedDate", "");
    formikRef.current?.setFieldValue("editedAssessmentTime", "");
  };

  const updateAssessmentDate = ({ assessment_date, assessment_time }) => {
    if (!assessment_date || (assessment_date !== editingDate && assessmentDates.some((date) => date.assessment_date === assessment_date))) return;

    setAssessmentDates((dates) => dates.map((date) => (
      date.assessment_date === editingDate ? { assessment_date, assessment_time } : date
    )));
    cancelEditingDate();
  };

  return (
    <Modal
      className="transparentModal common-confirmation-modal modal-v2"
      show={show}
      onHide={onHide}
      centered
    >
      <Formik
        innerRef={formikRef}
        initialValues={{ newAssessmentDate: "", newAssessmentTime: "", editedDate: "", editedAssessmentTime: "" }}
        onSubmit={() => onSave(assessmentDates)}
      >
        {({ values }) => (
          <Form>
            <Modal.Body>
              <div
                className="section-title d-flex align-items-center justify-content-between"
                style={{ borderBottom: "1px solid #315D99" }}
              >
                <h3 className="mb-0 pb-0 section-title border-bottom-0" style={{ fontSize: "14px" }}>
                  ADD ASSESSMENT DATE
                </h3>
                <img
                  onClick={onHide}
                  width={16}
                  height={16}
                  src={crossIcon}
                  alt="crossIcon"
                  style={{ cursor: "pointer" }}
                />
              </div>
              <div className="d-flex gap-2 align-items-end">
                <div className="flex-grow-1 align-items-center">
                  <label className="form-label mb-1">Assessment date</label>
                  <AppDatePicker
                    name="newAssessmentDate"
                    className="readonly-box w-100"
                    placeholderText="Select assessment date"
                  />
                </div>
                <div className="flex-grow-1 align-items-center">
                  <label className="form-label mb-1" htmlFor="newAssessmentTime">Assessment time (optional)</label>
                  <AppInputField
                    id="newAssessmentTime"
                    type="time"
                    name="newAssessmentTime"
                    style={{height: "38px"}}
                    className="readonly-box w-100"
                  />
                </div>
                <CommonButton
                  type="button"
                  style={{ color: "#315D99", border: "none", cursor: (!values.newAssessmentDate || assessmentDates.some((date) => date.assessment_date === values.newAssessmentDate)) ? "not-allowed" : "pointer", paddingLeft: "0", paddingRight: "0" }}
                  onClick={() => addAssessmentDate({ assessment_date: values.newAssessmentDate, assessment_time: values.newAssessmentTime })}
                  disabled={!values.newAssessmentDate || assessmentDates.some((date) => date.assessment_date === values.newAssessmentDate)}
                  title="Add assessment date"
                  text={<PlusCircle />}
                  aria-label="Add assessment date"
                />
              </div>

              {assessmentDates.length > 0 ? (
                <div className="mt-3 d-flex flex-column gap-2">
                  {assessmentDates.map((date) => (
                    <div key={date.assessment_date} className="d-flex justify-content-between align-items-center border-bottom pb-2">
                      {editingDate === date.assessment_date ? (
                        <div className="d-flex gap-2 flex-grow-1">
                          <AppDatePicker name="editedDate" className="readonly-box" aria-label={`Edit ${date.assessment_date}`} />
                          <AppInputField type="time" name="editedAssessmentTime" className="readonly-box" aria-label={`Edit assessment time for ${date.assessment_date}`} />
                        </div>
                      ) : (
                        <span>{dateFormat(date.assessment_date, "MMM DD, YYYY")}{date.assessment_time ? ` at ${dateFormat(date.assessment_time, "hh:mm A", "hh:mm")}` : ""}</span>
                      )}
                      <div className="d-flex align-items-center gap-2 ms-2">
                        {editingDate === date.assessment_date ? (
                          <>
                            <Button
                              type="button"
                              variant="transparent"
                              className="p-0"
                              style={{ color: "#315D99" }}
                              aria-label={`Save ${date.assessment_date}`}
                              title="Apply date change"
                              disabled={!values.editedDate || (values.editedDate !== date.assessment_date && assessmentDates.some((item) => item.assessment_date === values.editedDate))}
                              onClick={() => updateAssessmentDate({ assessment_date: values.editedDate, assessment_time: values.editedAssessmentTime })}
                            >
                              <Check size={17} />
                            </Button>
                            <Button
                              type="button"
                              variant="transparent"
                              className="p-0"
                              style={{ color: "#315D99" }}
                              aria-label={`Cancel editing ${date.assessment_date}`}
                              title="Cancel date change"
                              onClick={cancelEditingDate}
                            >
                              <X size={17} />
                            </Button>
                          </>
                        ) : (
                          <Button
                            type="button"
                            variant="transparent"
                            className="p-0"
                            style={{ color: "#315D99" }}
                            aria-label={`Edit ${date.assessment_date}`}
                            title="Edit assessment date"
                            onClick={() => startEditingDate(date)}
                          >
                            <Edit2 size={16} />
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="transparent"
                          className="p-0"
                          style={{ color: "#315D99" }}
                          aria-label={`Remove ${date.assessment_date}`}
                          title="Remove assessment date"
                          disabled={editingDate === date.assessment_date}
                          onClick={() => setAssessmentDates((dates) => dates.filter((item) => item.assessment_date !== date.assessment_date))}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-3 mb-0 text-muted">No assessment dates selected.</p>
              )}
              <div className="d-flex justify-content-end mt-4 gap-2">
                <CommonButton
                  type="button"
                  onClick={onHide}
                  style={{ color: "#315D99", background: "#fff", border: "1px solid #315D99" }}
                  text="Cancel"
                />
                <CommonButton
                  type="submit"
                  disabled={!assessmentDates.length || editingDate || isLoading}
                  style={{ color: "#fff", background: "#315D99" }}
                  text="Save"
                />
              </div>
            </Modal.Body>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AssessmentDatesModal;
